import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getUserRole } from '../utils/roleHierarchy';
import { useModal } from '../context/ModalContext';
import Layout from './Layout';
import CalendarView from './CalendarView';
import MeetingsPanel from './MeetingsPanel';
import AgencyHubPanel from './AgencyHubPanel';
import ToDoList from './ToDoList';
import ContentCreation from './ContentCreation';
import CreateIdeas from './CreateIdeas';
import Recordings from './Recordings';
import LiveKitRecordings from './LiveKitRecordings';
import { LIVEKIT_ENABLED } from '../config/features';
import Gemini from './Gemini';
import IdeasPanel from './IdeasPanel';
import ProxyUserAdmin from './ProxyUserAdmin';
import BrandAssets from './BrandAssets';
import InvitationsPanel from './InvitationsPanel';
import OrganizationsBrandsPanel from './OrganizationsBrandsPanel';
import DashboardHome from './DashboardHome';
import ActivityLogPanel from './ActivityLogPanel';
import ReviewContentPanel from './ReviewContentPanel';
import SchedulePostsPanel from './SchedulePostsPanel';
import SourcesPanel from './SourcesPanel';
import ManagementPanel from './ManagementPanel';
import ProfilePanel from './ProfilePanel';
import NotificationsPanel from './NotificationsPanel';
import WelcomeModal from './WelcomeModal';
import OnboardingWizard from './OnboardingWizard';
import OnboardingRoleSelector from './OnboardingRoleSelector';
import DatabaseBackups from './DatabaseBackups';
import '../styles/AdminDashboard.css';

const AdminDashboard = ({ user, onLogout, onUserUpdate, isProxyMode = false }) => {
  const modal = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [showOnboardingRoleSelector, setShowOnboardingRoleSelector] = useState(false);
  const [selectedOnboardingRole, setSelectedOnboardingRole] = useState(null);

  // In proxy mode, use local state instead of URL params to avoid affecting parent
  const [localActiveSection, setLocalActiveSection] = useState('dashboard');
  const [userMgmtSection, setUserMgmtSection] = useState(() => {
    return localStorage.getItem('admin_user_mgmt_tab') || 'invitations';
  });

  useEffect(() => {
    localStorage.setItem('admin_user_mgmt_tab', userMgmtSection);
  }, [userMgmtSection]);

  // Read activeSection from URL params (if not in proxy mode) or local state (if in proxy mode)
  // When in proxy mode, ignore URL params to prevent interference with parent dashboard
  const activeSection = isProxyMode ? localActiveSection : (searchParams.get('section') || 'dashboard');

  // Update URL or local state when section changes
  const setActiveSection = async (section) => {
    // If navigating away from the proxy panel, always clear proxy session first
    if (!isProxyMode && activeSection === 'proxy-user' && section !== 'proxy-user') {
      try {
        await axios.post('/api/admin/clear-proxy', {}, { withCredentials: true });
      } catch (e) {}
    }
    if (isProxyMode) {
      // In proxy mode, only update local state - don't touch URL
      setLocalActiveSection(section);
    } else {
      setSearchParams({ section });
    }
  };

  const userRole = getUserRole(user);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (user && !isProxyMode) {
      if (user.welcome_seen === false) {
        setShowWelcomeModal(true);
      } else if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    }
  }, [user, isProxyMode]);

  const handleOpenWelcome = () => {
    setShowWelcomeModal(true);
  };

  const handleWelcomeComplete = async () => {
    try {
      await axios.post('/api/user/mark-welcome-seen');
      if (user.onboarding_seen === false) {
        setShowOnboardingWizard(true);
      }
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const handleOpenOnboarding = () => {
    setShowOnboardingWizard(true);
  };

  const handleRoleSelect = (role) => {
    setSelectedOnboardingRole(role);
    setShowOnboardingWizard(true);
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error in proxy mode as this may be a brief transition error
      if (!isProxyMode) {
        modal.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh - updates data without showing the full loading spinner,
  // which would unmount panels and reset their sub-panel state
  const refreshDashboardData = async () => {
    try {
      const response = await axios.get('/api/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    }
  };

  const handleOrganizationChange = async (organization) => {
    try {
      // Organization context is already set on backend via OrganizationSelector
      // Just refresh dashboard data with new filter
      fetchDashboardData();
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const getContextMenuTitle = () => {
    switch (activeSection) {
      case 'dashboard':
        return 'Dashboard';
      case 'todo':
        return 'To Do List';
      case 'create':
        return 'Create Content';
      case 'admin-user-management':
        return 'User Management';
      case 'admin-organizations-brands':
        return 'Organization';
      case 'gemini':
        return 'Velvet AI';
      case 'calendar':
      case 'calendar-scheduler':
        return 'Calendar';
      case 'create-ideas':
        return 'Create Ideas';
      case 'operator-review-ideas':
        return 'Review Ideas';
      case 'review-content':
        return 'Review Content';
      case 'recordings':
        return 'Recordings';
      case 'velvet-recordings':
        return 'Velvet Recordings';
      case 'sources':
        return 'Manage Sources';
      case 'proxy-user':
        return 'Proxy User';
      case 'assets':
        return 'Brand Identity';
      case 'activity-log':
        return 'Activity Logs';
      case 'database-backups':
        return 'Database Backups';
      case 'agency-hub':
        return 'Agency Hub';
      default:
        return 'Dashboard';
    }
  };


  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'todo':
        return <ToDoList user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'create':
        return <ContentCreation user={user} />;
      case 'recordings':
        return <Recordings user={user} />;
      case 'velvet-recordings':
        return LIVEKIT_ENABLED
          ? <LiveKitRecordings user={user} />
          : <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
      case 'sources':
        return <SourcesPanel user={user} />;
      case 'calendar':
      case 'calendar-scheduler':
        return <MeetingsPanel user={user} />;
      case 'admin-user-management':
        return renderUserManagementWithNav();
      case 'admin-organizations-brands':
        return renderOrganizationsBrands();
      case 'gemini':
        return <Gemini user={user} />;
      case 'create-ideas':
        return <CreateIdeas user={user} />;
      case 'operator-review-ideas':
        return <IdeasPanel user={user} />;
      case 'review-content':
        return <ReviewContentPanel user={user} />;
      case 'schedule-posts':
        return <SchedulePostsPanel user={user} />;
      case 'proxy-user':
        return <ProxyUserAdmin user={user} onLogout={onLogout} dashboardData={dashboardData} />;
      case 'assets':
        return <BrandAssets user={user} />;
      case 'activity-log':
        return <ActivityLogPanel user={user} />;
      case 'database-backups':
        return <DatabaseBackups user={user} />;
      case 'agency-hub':
        return <AgencyHubPanel user={user} onRefresh={refreshDashboardData} />;
      case 'profile':
        return <ProfilePanel user={user} onUserUpdate={onUserUpdate} />;
      case 'notifications':
        return <NotificationsPanel user={user} />;
      default:
        // Default to dashboard home for any unrecognized sections
        return <DashboardHome user={user} onSectionChange={setActiveSection} isProxyMode={isProxyMode} />;
    }
  };

  const renderUserManagementWithNav = () => {
    const renderUserMgmtContent = () => {
      switch (userMgmtSection) {
        case 'management':
          return <ManagementPanel user={user} dashboardData={dashboardData} onRefresh={refreshDashboardData} />;
        case 'invitations':
        default:
          return (
            <InvitationsPanel
              user={user}
              dashboardData={dashboardData}
              isSuperAdmin={false}
            />
          );
      }
    };

    return (
      <div className="super-admin-internal-layout">
        <nav className="internal-nav">
          <div className="internal-nav-items">
            <button
              className={`internal-nav-btn ${userMgmtSection === 'invitations' ? 'active' : ''}`}
              onClick={() => setUserMgmtSection('invitations')}
              data-onboarding="invitations-tab"
            >
              Invitations
            </button>
            <button
              className={`internal-nav-btn ${userMgmtSection === 'management' ? 'active' : ''}`}
              onClick={() => setUserMgmtSection('management')}
              data-onboarding="management-tab"
            >
              Management
            </button>
          </div>
        </nav>
        <div className="internal-content">
          {renderUserMgmtContent()}
        </div>
      </div>
    );
  };

  const renderOrganizationsBrands = () => (
    <OrganizationsBrandsPanel
      user={user}
      dashboardData={dashboardData}
      isSuperAdmin={false}
      onRefresh={refreshDashboardData}
    />
  );

  if (loading) {
    return (
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        isProxyMode={isProxyMode}
        showOrgSelector={activeSection !== 'dashboard'}
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        contextMenuContent={<h2>{getContextMenuTitle()}</h2>}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        onOpenOnboarding={handleOpenOnboarding}
        isProxyMode={isProxyMode}
        showOrgSelector={activeSection !== 'dashboard'}
      >
        {renderContent()}
      </Layout>

      {!isProxyMode && (
        <>
          <WelcomeModal
            isOpen={showWelcomeModal}
            onClose={() => setShowWelcomeModal(false)}
            onComplete={handleWelcomeComplete}
          />
          <OnboardingRoleSelector
            isOpen={showOnboardingRoleSelector}
            onClose={() => setShowOnboardingRoleSelector(false)}
            onSelectRole={handleRoleSelect}
          />
          <OnboardingWizard
            isOpen={showOnboardingWizard}
            onClose={() => {
              setShowOnboardingWizard(false);
              setSelectedOnboardingRole(null);
            }}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
            user={user}
            overrideRole={selectedOnboardingRole}
            onSectionChange={setActiveSection}
            currentSection={activeSection}
          />
        </>
      )}
    </>
  );
};

export default AdminDashboard;
